import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  OnDestroy,
  OnInit,
  Optional,
  Self
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";

import { AppAddressService } from "@onsip/common/services/api/resources/appAddress/app-address.service";
import {
  AddressSummary,
  AddressSummaryService
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { TranslateService } from "@ngx-translate/core";
import { ApiPromiseSubject } from "@onsip/common/services/api/api-promise-state.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";

export function valueStartSymbolsValidator(): ValidatorFn {
  const reg = new RegExp("^[A-Za-z]");

  return (control: AbstractControl): ValidationErrors | null => {
    const forbidden = !reg.test(control.value);
    // eslint-disable-next-line no-null/no-null
    return forbidden ? { wrongBeginning: true } : null;
  };
}

export function valueEndSymbolsValidator(): ValidatorFn {
  const reg = /[a-zA-Z0-9]$/;
  return (control: AbstractControl): ValidationErrors | null => {
    const forbidden = !reg.test(control.value);
    // eslint-disable-next-line no-null/no-null
    return forbidden ? { wrongEnding: true } : null;
  };
}

export function valueConsecutiveSigns(): ValidatorFn {
  const reg = /(\.\.)|(--)|(__)/;
  return (control: AbstractControl): ValidationErrors | null => {
    const forbidden = reg.test(control.value);
    // eslint-disable-next-line no-null/no-null
    return forbidden ? { consecutiveSigns: true } : null;
  };
}

const CONTROL_VALIDATORS = [
  Validators.required,
  Validators.maxLength(30),
  Validators.pattern("^[a-zA-Z_a-zA-Z0-9-\\.]*(\\.[_a-zA-Z0-9-\\.]+)*([a-zA-Z0-9]+)$"),
  valueStartSymbolsValidator(),
  valueEndSymbolsValidator(),
  valueConsecutiveSigns()
];

const dotRegEx = new RegExp(/[.]+/g);

@Component({
  selector: "onsip-sip-address-field",
  templateUrl: "./sip-address-field.component.html",
  styleUrls: ["./sip-address-field.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SipAddressFieldComponent
  implements ControlValueAccessor, OnInit, OnDestroy, DoCheck, Validator
{
  private unsubscriber = new Subscription();
  private baseFieldValue!: string;
  private addresses!: Array<AddressSummary>;

  E2eLocators = E2eLocators;

  domain$: BehaviorSubject<string> = new BehaviorSubject<string>("onsip.com");

  domWidth = "";
  fieldStyle = "";

  onTouched!: Function;
  controlValue = new FormControl("", { nonNullable: true, validators: CONTROL_VALIDATORS });
  errorMessage$: BehaviorSubject<string> = new BehaviorSubject<string>("");

  constructor(
    private appAddress: AppAddressService,
    private addressSummaryService: AddressSummaryService,
    private translate: TranslateService,
    @Optional() @Self() public ngControl: NgControl,
    private cdr: ChangeDetectorRef
  ) {
    // eslint-disable-next-line no-null/no-null
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngDoCheck(): void {
    if (this.ngControl?.touched && this.controlValue.untouched) {
      this.controlValue.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    this.initDomain();
    if (this.ngControl?.control) {
      this.ngControl.control.setValidators(CONTROL_VALIDATORS);
      this.ngControl.control.updateValueAndValidity();
      this.setErrorMessage();
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  updateAppAddress(aor: string): Promise<ApiPromiseSubject<AddressSummary>> {
    return this.appAddress
      .appAddressEdit(aor, {
        Username: this.controlValue.value
      })
      .then(result => {
        if (result.status === "success") {
          // the result param from app address is kinda useless as it does not show sip address or username
          // we need to get it from addressSummary again by refetching
          return this.addressSummaryService.addressSummaryBrowse();
        } else {
          this.appAddress.clearErrors();
          throw Error(result.data.message);
        }
      });
  }

  registerOnChange(onChanged: Function): void {
    this.unsubscriber.add(
      this.controlValue.valueChanges.subscribe(value => {
        onChanged(value);
        this.checkUserNameExistence();
        if (this.controlValue.errors) {
          this.setErrorMessage();
        }
      })
    );
  }

  registerOnTouched(fn: Function) {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    value = this.replaceSpacesAndDots(value);
    value = value.toLowerCase();
    this.baseFieldValue = value;
    this.controlValue?.setValue(value);
    if (value) {
      this.controlValue.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  private replaceSpacesAndDots(value: string): string {
    const newValue = value
      .trim()
      .replace(/[^A-Z0-9.]/gi, ".")
      .replace(dotRegEx, ".");
    return newValue;
  }

  validate(): { [key: string]: any } | null {
    return this.controlValue.errors;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.controlValue?.disable();
    } else {
      this.controlValue?.enable();
    }
  }

  setErrorMessage(): void {
    this.errorMessage$.next(this.getGenericSipAddressError("SIP address"));
    this.ngControl?.control?.setErrors(this.controlValue.errors);
  }

  checkUserNameExistence(): void {
    const isUserNameExist = this.addresses?.some(
      address => address.username === this.controlValue.value
    );
    if (isUserNameExist) {
      this.controlValue.setErrors({ alreadyExist: true });
    }
  }

  getGenericSipAddressError(value: string): string {
    return this.controlValue.hasError("required")
      ? this.translate.instant("ONSIP_I18N.SIP_ADDRESS_IS_REQUIRED")
      : this.controlValue.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.VALUE_IS_LONGER_THAN_LENGTH_CHARACTERS", {
          value,
          length: this.controlValue.errors?.maxlength.requiredLength
        })
      : this.controlValue.hasError("alreadyExist")
      ? this.translate.instant("ONSIP_I18N.SIP_ADDRESS_ALREADY_EXISTS")
      : this.controlValue.hasError("wrongBeginning")
      ? this.translate.instant("ONSIP_I18N.CAN_NOT_START_WITH_SYMBOLS_AND_NUMBERS")
      : this.controlValue.hasError("wrongEnding")
      ? this.translate.instant("ONSIP_I18N.CAN_NOT_ENDS_WITH_SYMBOLS")
      : this.controlValue.hasError("consecutiveSigns")
      ? this.translate.instant("ONSIP_I18N.CANT_HAVE_CONSECUTIVE___")
      : this.controlValue.hasError("pattern")
      ? this.translate.instant("ONSIP_I18N.CAN_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_AND___", {
          value
        })
      : "";
  }

  private initDomain(): void {
    this.unsubscriber.add(
      this.addressSummaryService.orgState.subscribe(addressSummaries => {
        if (addressSummaries.length === 0) {
          this.addressSummaryService.addressSummaryBrowse();
        }
        this.addresses = addressSummaries?.filter(
          address => address.username !== this.baseFieldValue
        );
        if (this.addresses?.length) {
          this.domain$.next(this.addresses[0]?.domain);
          this.setFormWidths();
        }
      })
    );
  }

  private setFormWidths(): void {
    this.domWidth = (this.getTextWidth(this.domain$.value) + 25).toString() + "px";
    this.fieldStyle = `width: calc(100% - ${this.domWidth})`;
  }

  private getTextWidth(text: string) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context) {
      const suffixEl = document.getElementById("suffix");
      context.font = suffixEl
        ? getComputedStyle(suffixEl).font
        : "300 14px / 20px Roboto, sans-serif";
      return context.measureText(text).width;
    }
    return 127;
  }
}
